import { pushWithRegion } from 'actions/NavigationActions'
import Clickable from 'components/Common/Clickable'
import ResponsiveImage from 'components/Common/ResponsiveImage'
import LineFocusIcon from 'LuxKit/Icons/line/LineFocusIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import ModalContext from 'contexts/ModalContext'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { useAppDispatch } from 'hooks/reduxHooks'
import { rem } from 'polished'
import { memo, useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { getTourV2OfferPageBaseUrl } from 'selectors/TourV2/tourV2OfferPageSelectors'
import styled from 'styled-components'
import TourV2Itinerary from './TourV2Itinerary'
import TourV2RouteMapModal from './TourV2RouteMapModal'

const Root = styled(Group)`
  > .itinerary-area {
    flex-grow: 1;
  }

  > .map-area {
    flex-shrink: 0;
  }
`

const RouteMapModalActivator = styled(Clickable)`
  display: flex;
  border-radius: ${props => props.theme.borderRadius.M};
  overflow: hidden;
  position: relative;

  ${mediaQueryUp.tablet} {
    max-width: ${rem(457)};
    max-height: ${rem(685)};
    position: sticky;
    top: ${rem(130)}; /* sticky header height (118) + margin (12) */
  }
`

const ExpandBadge = styled.div`
  display: flex;
  position: absolute;
  border-radius: ${props => props.theme.borderRadius.S};
  inset-inline-end: 0;
  bottom: 0;
  margin: ${rem(8)};
  gap: ${rem(8)};
  padding: ${rem(8)};
  background-color: rgba(0, 0, 0, .8);
`
interface Props {
  offerType: App.Tours.V2OfferType
  itinerary: Array<App.Tours.TourV2OfferItineraryItem>
  variation?: App.Tours.TourV2OfferVariation
  alternativeVariation?: App.Tours.TourV2OfferVariation
  onAlternativeRouteChange: (variationId: string) => void
}

interface MappedProps {
  baseTourOfferUrl: string
}

function TourV2ItineraryAndMap(props: Props & MappedProps) {
  const { itinerary, variation, offerType, alternativeVariation, onAlternativeRouteChange, baseTourOfferUrl } = props
  const intl = useIntl()
  const routeMapImage = variation?.routeMapImage

  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const changeAlternativeVariation = useCallback(() => {
    if (alternativeVariation) {
      offerPageDispatch({ type: OfferPageActions.setTourV2Departure, departure: undefined })
      dispatch(pushWithRegion(
        `${baseTourOfferUrl}/${alternativeVariation.id}`,
        '',
        { scrollBehaviour: 'smooth', scrollTo: '#TOUR_V2_CONTENT_TOP_ANCHOR' },
      ))
      offerPageDispatch({
        type: OfferPageActions.setTourV2Variation,
        variation: alternativeVariation,
      })
      onAlternativeRouteChange(alternativeVariation.id)
    }
  },
  [offerPageDispatch, dispatch, baseTourOfferUrl, onAlternativeRouteChange, alternativeVariation],
  )

  const showModal = useContext(ModalContext)

  const openMapModal = useCallback(() => {
    showModal(<TourV2RouteMapModal variation={variation!} />)
  }, [showModal, variation])

  return <Root direction="vertical-reverse" gap={32} tabletDirection="horizontal">
    <div className="itinerary-area">
      <TourV2Itinerary
        itinerary={itinerary}
        title={intl.formatMessage({
          id: 'ZubnwjKArDObxJWqtJ4-',
          defaultMessage: 'What you’ll see',
          description: 'Heading for tour itinerary section on the tour offer detail page',
        })}
        offerType={offerType}
        alternativeVariation={alternativeVariation}
        changeAlternativeVariation={changeAlternativeVariation}
      />
    </div>

    {!!routeMapImage && <div className="map-area">
      <RouteMapModalActivator onClick={openMapModal}>
        <ResponsiveImage
          id={routeMapImage.id!}
          alt={routeMapImage.title}
          tabletWidth="370px"
          desktopWidth="457px"
          mobileAspectRatio="1:1"
          tabletAspectRatio={undefined}
        />

        <ExpandBadge>
          <LineFocusIcon size="S" colour="dark-contrast" />
          <BodyText variant="medium" colour="dark-contrast">{intl.formatMessage({
            id: 'untDSj8ao-A1EU770-oP',
            defaultMessage: 'Expand map',
            description: 'Button text to expand route map in modal on the map view',
          })}
          </BodyText>
        </ExpandBadge>
      </RouteMapModalActivator>
    </div>}

  </Root>
}

const mapOfferPageStateToProps = (offerPageState: OfferPageState): MappedProps => ({
  baseTourOfferUrl: getTourV2OfferPageBaseUrl(offerPageState),
})

export default memo(withOfferPageState(mapOfferPageStateToProps, TourV2ItineraryAndMap))
